/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {useIntl, FormattedMessage} from 'react-intl'

// Chakra Components
import {Box, Flex, HStack, Stack, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Progress} from '@chakra-ui/react'

// Project Components
import ItemVariantProvider from '../../../components/item-variant'
import CartItemVariantImage from '../../../components/item-variant/item-image'
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner'

// Hooks
import {useProduct} from '@salesforce/commerce-sdk-react'

/**
 * ApproachingPromotion displays the product and promotion information as user approaches
 * the required amount to redeem the promotion
 *
 * @param product - product information of the gift product
 * @param promotionThreshold - required spending amount to redeem the promotion
 * @param basketValue - basket value
 * @param showLoading showLoading - to show loading spinner
 * @return {JSX.Element}
 */
const ApproachingPromotion = ({
    productId,
    promotionThreshold,
    basketValue,
    showLoading = false,
    currency,
    ...props
}) => {
    const intl = useIntl()
    const {data: product} = useProduct({
        parameters: {
            id: productId
        }
    })

    return (
        <Box
            position="relative"
            p={['24px 16px', null, '32px 40px', '24px 16px']}
            bg="white"
            border="1px solid"
            borderColor={'borderGray'}
            {...props}
        >
            {product && (
                <ItemVariantProvider variant={product}>
                    {showLoading && <LoadingSpinner />}
                    <Flex width="full">
                        <CartItemVariantImage
                            width={['85px', null, '147px', '85px']}
                            mr={[16, null, 24, 16]}
                        />
                        <Box flex={1}>
                            <Stack gap={0}>
                                <Text
                                    fontFamily={'heading'}
                                    fontSize="2xl"
                                    lineHeight={'19px'}
                                    textTransform={'uppercase'}
                                >
                                    <FormattedMessage
                                        defaultMessage="Claim your free phone case with any purchase of {price} or more"
                                        id="approaching_promotion.label.promotion_claim"
                                        values={{
                                            price: intl.formatNumber(promotionThreshold, {
                                                style: 'currency',
                                                currency: currency,
                                                minimumFractionDigits: 0
                                            })
                                        }}
                                    />
                                </Text>
                                <Text variant="bodySmall" color="darkGray" mt={16}>
                                    <FormattedMessage
                                        defaultMessage="Only {remainingValue} left to get your free gift"
                                        id="approaching_promotion.label.promotion_remaining_value"
                                        values={{
                                            remainingValue: (
                                                <Text as="span" color="fullBlack">
                                                    {intl.formatNumber(
                                                        promotionThreshold - basketValue,
                                                        {
                                                            style: 'currency',
                                                            currency: currency,
                                                            minimumFractionDigits: 0
                                                        }
                                                    )}
                                                </Text>
                                            )
                                        }}
                                    />
                                </Text>
                                <HStack mt={'14px'}>
                                    <Progress
                                        value={(basketValue * 100) / promotionThreshold}
                                        flex={1}
                                        height={'4px'}
                                        colorScheme={'pink'}
                                        bgColor={'whiteSmoke'}
                                        aria-label={intl.formatMessage({
                                            defaultMessage: 'Promotion value progress',
                                            id: 'approaching_promotion.aria_label.progress'
                                        })}
                                    />
                                    <Text variant={'bodyLarge1'}>
                                        {intl.formatNumber(promotionThreshold, {
                                            style: 'currency',
                                            currency: currency,
                                            minimumFractionDigits: 0,
                                            currencyDisplay: 'symbol'
                                        })}
                                    </Text>
                                </HStack>
                            </Stack>
                        </Box>
                    </Flex>
                </ItemVariantProvider>
            )}
        </Box>
    )
}

ApproachingPromotion.propTypes = {
    productId: PropTypes.string,
    promotionThreshold: PropTypes.number.isRequired,
    basketValue: PropTypes.number.isRequired,
    showLoading: PropTypes.bool,
    currency: PropTypes.string
}

export default ApproachingPromotion
